import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react"
import type { VehicleCard } from "../types/api"
import { useI18n } from "../hooks/useI18n"
import { garageUi, type GarageUiKey } from "../i18n/garageUi"
import { FigmaLicensePlate, formatCompactCount } from "./MainFigmaVisuals"
import { AppHeader } from "./AppHeader"
import { garageActivationNote, garageAsset, garageInactiveLock } from "./GarageAssetLibrary"
import { resolveStatusFrame, type StatusFramePresentation } from "./StatusFrameResolver"
import { FINAL_STATUS_VISUALS } from "./StatusVisuals"
import { DEMO_PLATE_NORMALIZED } from "../utils/plate"
import "./GaragePage.figma.css"
import "./GarageQuickCapsules.css"

function garageMaskStyle(src: string): CSSProperties {
  return { "--bbf-garage-icon-mask": `url("${src}")` } as CSSProperties
}

type NavigatorWithVirtualKeyboard = Navigator & {
  virtualKeyboard?: {
    boundingRect?: { height: number }
    addEventListener?: (type: "geometrychange", listener: EventListener) => void
    removeEventListener?: (type: "geometrychange", listener: EventListener) => void
  }
}

const GARAGE_REFERENCE_BODY_HEIGHT = 710
const GARAGE_VISUAL_INSET = 16
const DEMO_GARAGE_VIEWS = 99000
// Kept for a later Garage release. Flip to true to restore the five quick actions.
const GARAGE_QUICK_CAPSULES_ENABLED = false
const GARAGE_QUICK_CAPSULES = [
  "quick.parking",
  "quick.note",
  "quick.mark",
  "quick.alert",
  "quick.expenses",
] as const satisfies readonly GarageUiKey[]
const DEMO_AUTOCLAN_VISUAL = FINAL_STATUS_VISUALS.autoclan
const DEMO_INACTIVE_STATUS_FRAME: StatusFramePresentation = {
  label: "BeepApp",
  gradient: `linear-gradient(90deg, ${DEMO_AUTOCLAN_VISUAL.fill[1]} 0%, ${DEMO_AUTOCLAN_VISUAL.fill[0]} 50%, ${DEMO_AUTOCLAN_VISUAL.fill[1]} 100%)`,
  stops: [
    { offset: 0, color: DEMO_AUTOCLAN_VISUAL.fill[1] },
    { offset: 0.5, color: DEMO_AUTOCLAN_VISUAL.fill[0] },
    { offset: 1, color: DEMO_AUTOCLAN_VISUAL.fill[1] },
  ],
}

export const GARAGE_ACTIONS = [
  { labelKey:"action.logbook", icon:"journal", className:"journal", requiresActive:true, lock:true, accessory:"chevron" },
  { labelKey:"action.status", icon:"status", className:"status", requiresActive:true, lock:true, accessory:"chevron" },
  { labelKey:"action.autoclan", icon:"autoclan", className:"autoclan", requiresActive:true, lock:true, accessory:"chevron" },
  { labelKey:"action.privacy", icon:"privacy", className:"privacy", requiresActive:true, lock:true, accessory:"toggle" },
  { labelKey:"action.cleanStart", icon:"clean-start", className:"clean-start", requiresActive:true, lock:true, accessory:"chevron" },
  { labelKey:"action.autotrade", icon:"autotrade", className:"autotrade", requiresActive:true, accessory:"chevron", hideChevronWhenActive:true },
  { labelKey:"action.insurance", icon:"insurance", className:"insurance", requiresActive:true, accessory:"chevron", hideChevronWhenActive:true },
  { labelKey:"action.pitstop", icon:"pitstop", className:"pitstop", requiresActive:true, accessory:"chevron", hideChevronWhenActive:true },
  { labelKey:"action.techhelp", icon:"autohelp", className:"autohelp", requiresActive:true, accessory:"chevron", hideChevronWhenActive:true },
  { labelKey:"action.companion", icon:"companion", className:"companion", accessory:"chevron", hideChevronWhenActive:true },
  { labelKey:"action.dispatcher", icon:"bb-help", className:"bb-help", accessory:"chevron", hideChevronWhenActive:true },
] as const satisfies ReadonlyArray<{
  labelKey: GarageUiKey
  icon: string
  className: string
  requiresActive?: boolean
  lock?: boolean
  accessory: "chevron" | "toggle"
  hideChevronWhenActive?: boolean
}>

type Props = {
  vehicle: VehicleCard
  isIOS: boolean
  active: boolean
  vehicleBound?: boolean
  privacyOn?: boolean
  activationNote?: string
  actionHandlers?: Partial<Record<(typeof GARAGE_ACTIONS)[number]["className"], () => void>>
  onBack?: () => void
  headerRightActionLabel?: string
  onHeaderRightAction?: () => void
  stopDisabled?: boolean
  startDisabled?: boolean
  startLabel?: string
  onStop?: () => void
  onStart?: () => void
  children?: ReactNode
}

export function GarageBackdrop({
  vehicle,
  isIOS,
  active,
  vehicleBound = true,
  privacyOn = false,
  activationNote = "Активируйте ваш гараж",
  actionHandlers = {},
  onBack,
  headerRightActionLabel,
  onHeaderRightAction,
  stopDisabled = !active,
  startDisabled = active,
  startLabel = "Старт",
  onStop,
  onStart,
  children,
}: Props) {
  const { locale, direction, t } = useI18n()
  const interactiveState = active
  const unboundActive = active && !vehicleBound
  const platform = isIOS ? "ios" : "android"
  const state = active ? (privacyOn ? "active-on" : "active-off") : "inactive"
  const statusFrame = !active && vehicle.plate_normalized === DEMO_PLATE_NORMALIZED
    ? DEMO_INACTIVE_STATUS_FRAME
    : resolveStatusFrame(vehicle.current_status)
  const statusGradientId = `bbf-garage-status-${useId().replace(/:/g, "")}`
  const garageDisplayViews = vehicle.plate_normalized === DEMO_PLATE_NORMALIZED
    ? DEMO_GARAGE_VIEWS
    : vehicle.display_views
  const resolvedActivationNote = activationNote === "Активируйте ваш гараж"
    ? t("garageEntry.activationNote")
    : activationNote
  const resolvedStartLabel = startLabel === "Старт"
    ? garageUi(locale, "terminal.start")
    : startLabel === "Продлить"
      ? garageUi(locale, "terminal.renew")
      : startLabel
  const renewLabel = garageUi(locale, "terminal.renew")
  const terminalMode = active && resolvedStartLabel === renewLabel ? "renew" : active ? "stop" : "start"
  const terminalLabel = terminalMode === "renew"
    ? renewLabel
    : terminalMode === "stop"
      ? garageUi(locale, "terminal.stop")
      : resolvedStartLabel
  const terminalDisabled = terminalMode === "stop" ? stopDisabled : startDisabled
  const terminalAction = terminalMode === "stop" ? onStop : onStart

  const renderAction = (action: (typeof GARAGE_ACTIONS)[number]) => {
    const requiresActive = "requiresActive" in action && action.requiresActive
    const unavailable = Boolean(requiresActive && !active)
    const alwaysInteractive = !requiresActive
    const hideChevronWhenActive = "hideChevronWhenActive" in action && action.hideChevronWhenActive
    const showChevron = "accessory" in action &&
      action.accessory === "chevron" &&
      (interactiveState || alwaysInteractive) &&
      !(active && hideChevronWhenActive)
    const accessoryState = active ? state : "active-off"

    return (
      <button
        key={action.className}
        type="button"
        className={`bbf-garage-action bbf-garage-action-${action.className}`}
        aria-disabled={unavailable}
        onClick={unavailable ? undefined : actionHandlers[action.className]}
        tabIndex={actionHandlers[action.className] && !unavailable ? 0 : -1}
      >
        <span className="bbf-garage-action-left">
          <span
            className="bbf-garage-action-icon"
            style={garageMaskStyle(garageAsset(platform, state, action.icon))}
            aria-hidden="true"
          />
          <span dir={direction}>{garageUi(locale, action.labelKey)}</span>
        </span>

        {!active && "lock" in action && action.lock && (
          <img className="bbf-garage-action-lock" src={garageInactiveLock} alt="" />
        )}

        {showChevron && (
          <span
            className="bbf-garage-action-chevron"
            style={garageMaskStyle(garageAsset(platform, accessoryState, "chevron"))}
            aria-hidden="true"
          />
        )}

        {interactiveState && "accessory" in action && action.accessory === "toggle" && (
          <span className="bbf-garage-toggle" aria-hidden="true">
            <svg viewBox="0 0 42 26" focusable="false">
              <rect
                x="0"
                y="0"
                width="42"
                height="26"
                rx="13"
                fill={privacyOn ? "var(--bb-figma-action-primary)" : "var(--bb-figma-surface-toggle-off)"}
              />
              <circle
                cx={privacyOn ? 29 : 13}
                cy="13"
                r="10"
                fill="var(--bb-figma-plate-flag-white)"
              />
            </svg>
          </span>
        )}
      </button>
    )
  }

  const fitAreaRef = useRef<HTMLDivElement>(null)
  const [garageScale, setGarageScale] = useState(1)
  const [garageVisualWidth, setGarageVisualWidth] = useState(390)

  useEffect(() => {
    const root = document.documentElement
    const visualViewport = window.visualViewport
    const virtualKeyboard = (navigator as NavigatorWithVirtualKeyboard).virtualKeyboard
    let keyboardBaselineHeight = visualViewport?.height || window.innerHeight
    let keyboardWasVisible = false
    let scrollFrame = 0

    const setKeyboardVisible = (visible: boolean) => {
      const wasVisible = root.dataset.bbGarageKeyboard === "true"
      if (visible) root.dataset.bbGarageKeyboard = "true"
      else delete root.dataset.bbGarageKeyboard
      if (visible !== wasVisible) {
        window.dispatchEvent(new CustomEvent("bb:garage-keyboard", { detail: visible }))
      }
    }

    const isKeyboardField = (element: Element | null): element is HTMLInputElement | HTMLTextAreaElement => {
      if (element instanceof HTMLTextAreaElement) return true
      if (!(element instanceof HTMLInputElement)) return false
      return !["button", "checkbox", "color", "file", "hidden", "radio", "range", "reset", "submit"].includes(element.type)
    }

    const lockDocumentOrigin = () => {
      if (!window.scrollX && !window.scrollY) return
      window.cancelAnimationFrame(scrollFrame)
      scrollFrame = window.requestAnimationFrame(() => window.scrollTo(0, 0))
    }

    const syncKeyboardLock = () => {
      const activeElement = document.activeElement
      const currentVisualHeight = visualViewport?.height || window.innerHeight

      if (!isKeyboardField(activeElement)) {
        keyboardBaselineHeight = currentVisualHeight
        keyboardWasVisible = false
        setKeyboardVisible(false)
        lockDocumentOrigin()
        return
      }

      if (!keyboardWasVisible && currentVisualHeight > keyboardBaselineHeight) {
        keyboardBaselineHeight = currentVisualHeight
      }

      const viewportKeyboardHeight = Math.max(0, keyboardBaselineHeight - currentVisualHeight)
      const overlayKeyboardHeight = Math.max(0, Number(virtualKeyboard?.boundingRect?.height) || 0)
      const keyboardHeight = Math.max(viewportKeyboardHeight, overlayKeyboardHeight)

      if (keyboardHeight > 80) {
        keyboardWasVisible = true
        setKeyboardVisible(true)
      } else if (keyboardWasVisible && keyboardHeight < 40) {
        keyboardWasVisible = false
        activeElement.blur()
        setKeyboardVisible(false)
      } else if (!keyboardWasVisible) {
        setKeyboardVisible(false)
      }

      lockDocumentOrigin()
    }

    const syncAfterFocusChange = () => window.requestAnimationFrame(syncKeyboardLock)

    document.addEventListener("focusin", syncKeyboardLock)
    document.addEventListener("focusout", syncAfterFocusChange)
    window.addEventListener("scroll", lockDocumentOrigin, { passive: true })
    visualViewport?.addEventListener("resize", syncKeyboardLock)
    visualViewport?.addEventListener("scroll", lockDocumentOrigin)
    virtualKeyboard?.addEventListener?.("geometrychange", syncKeyboardLock)

    return () => {
      document.removeEventListener("focusin", syncKeyboardLock)
      document.removeEventListener("focusout", syncAfterFocusChange)
      window.removeEventListener("scroll", lockDocumentOrigin)
      visualViewport?.removeEventListener("resize", syncKeyboardLock)
      visualViewport?.removeEventListener("scroll", lockDocumentOrigin)
      virtualKeyboard?.removeEventListener?.("geometrychange", syncKeyboardLock)
      window.cancelAnimationFrame(scrollFrame)
      setKeyboardVisible(false)
      if (window.scrollX || window.scrollY) window.scrollTo(0, 0)
    }
  }, [])

  useLayoutEffect(() => {
    const area = fitAreaRef.current
    if (!area) return

    let lastVisibleHeight = 0

    const updateScale = () => {
      const viewport = area.parentElement
      if (!viewport) return

      const areaRect = area.getBoundingClientRect()
      const viewportRect = viewport.getBoundingClientRect()
      const topInset = Math.max(0, areaRect.top - viewportRect.top)
      const bottomInset = Math.max(0, viewportRect.bottom - areaRect.bottom)
      const leftInset = Math.max(0, areaRect.left - viewportRect.left)
      const rightInset = Math.max(0, viewportRect.right - areaRect.right)

      const visualWidth = window.visualViewport?.width || window.innerWidth || viewportRect.width
      const measuredVisualHeight = window.visualViewport?.height || window.innerHeight || viewportRect.height
      const activeElement = document.activeElement
      const keyboardInputFocused = activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement

      if (!keyboardInputFocused || measuredVisualHeight > lastVisibleHeight) {
        lastVisibleHeight = measuredVisualHeight
      }

      const visualHeight = keyboardInputFocused && lastVisibleHeight
        ? Math.max(lastVisibleHeight, measuredVisualHeight)
        : measuredVisualHeight

      const width = Math.max(0, Math.min(area.clientWidth, visualWidth - leftInset - rightInset))
      const height = Math.max(0, Math.min(area.clientHeight, visualHeight - topInset - bottomInset))
      if (!width || !height) return

      const next = Math.min(1, height / GARAGE_REFERENCE_BODY_HEIGHT)
      setGarageVisualWidth((current) => Math.abs(current - width) < 0.5 ? current : width)
      setGarageScale((current) => Math.abs(current - next) < 0.001 ? current : next)
    }

    updateScale()
    const observer = new ResizeObserver(updateScale)
    observer.observe(area)
    window.addEventListener("resize", updateScale)
    window.visualViewport?.addEventListener("resize", updateScale)

    return () => {
      observer.disconnect()
      window.removeEventListener("resize", updateScale)
      window.visualViewport?.removeEventListener("resize", updateScale)
    }
  }, [])

  const canvasWidth = garageVisualWidth / garageScale
  const canvasInset = GARAGE_VISUAL_INSET / garageScale

  return (
    <div className={`bbf-garage-screen ${isIOS ? "platform-ios" : "platform-android"} ${active ? "state-active" : "state-inactive"} ${unboundActive ? "state-unbound" : ""} ${privacyOn ? "privacy-on" : "privacy-off"}`}>
      <AppHeader
        left={{ label: t("common.back"), onClick: onBack }}
        right={headerRightActionLabel
          ? { label: headerRightActionLabel, onClick: onHeaderRightAction, tone: "danger" }
          : undefined}
      />

      <div className="bbf-garage-fit-area" ref={fitAreaRef}>
        <div
          className="bbf-garage-scale-box"
          style={{
            width: `${garageVisualWidth}px`,
            height: `${GARAGE_REFERENCE_BODY_HEIGHT * garageScale}px`,
          }}
        >
          <div
            className="bbf-garage-canvas"
            style={{
              width: `${canvasWidth}px`,
              height: `${GARAGE_REFERENCE_BODY_HEIGHT}px`,
              transform: `scale(${garageScale})`,
              "--bbf-garage-canvas-inset": `${canvasInset}px`,
              "--bbf-rail-width": `${canvasWidth}px`,
              "--bbf-rail-left": "0px",
              "--bbf-safe-bottom": "0px",
            } as CSSProperties}
          >
      <main className="bbf-garage-scroll">
        <section className="bbf-garage-vehicle-block">
          <div className={`bbf-garage-plate-stage ${statusFrame ? "with-status" : ""}`}>
            <div className="bbf-garage-plate">
              <FigmaLicensePlate plate={vehicle} size="garage" />
            </div>

            {statusFrame && (
              <>
                <svg
                  className="bbf-garage-status-frame"
                  viewBox="0 0 340.6846008300781 83.68461608886719"
                  preserveAspectRatio="none"
                  style={{ width: "342px" }}
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient id={statusGradientId} x1="0" y1="41.8423" x2="340.6846008300781" y2="41.8423" gradientUnits="userSpaceOnUse">
                      {statusFrame.stops.map((stop) => <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />)}
                    </linearGradient>
                  </defs>
                  <path d="M336.081 0C338.623 0.000160534 340.684 2.06113 340.685 4.60352V79.0811C340.684 81.6235 338.623 83.6844 336.081 83.6846H4.60352C2.06113 83.6844 0.00015379 81.6235 0 79.0811V4.60352C0.000178863 2.06114 2.06114 0.000178568 4.60352 0H336.081ZM5.78809 4.60352C5.13427 4.60352 4.60352 5.13427 4.60352 5.78809V66.5586C4.60359 67.2124 5.13431 67.7422 5.78809 67.7422H334.896C335.55 67.7422 336.081 67.2124 336.081 66.5586V5.78809C336.081 5.13427 335.55 4.60352 334.896 4.60352H5.78809Z" fill={`url(#${statusGradientId})`} />
                </svg>
                <span className="bbf-garage-status-label">{statusFrame.label}</span>
              </>
            )}
          </div>

          <div className="bbf-garage-meta">
            <span className="bbf-garage-view-cluster">
              <span className="bbf-garage-eye" style={garageMaskStyle(garageAsset(platform, state, "eye"))} aria-hidden="true" />
              <span className="bbf-garage-views">{formatCompactCount(garageDisplayViews)}</span>
            </span>
            <span
              className="bbf-garage-state"
              dir={direction}
              style={unboundActive ? { color: "var(--bb-figma-state-warning)" } : undefined}
            >
              {unboundActive
                ? garageUi(locale, "state.unbound")
                : active
                  ? garageUi(locale, "state.active")
                  : garageUi(locale, "state.inactive")}
            </span>
          </div>

          {!active && (
            <span className="bbf-garage-activate-note">
              <img src={garageActivationNote(platform)} alt="" />
              <span dir={direction}>{resolvedActivationNote}</span>
            </span>
          )}

          {active && GARAGE_QUICK_CAPSULES_ENABLED && (
            <div className="bbf-garage-quick-capsules" role="group" aria-label={garageUi(locale, "aria.quickActions")}>
              {GARAGE_QUICK_CAPSULES.map((labelKey) => {
                const label = garageUi(locale, labelKey)
                return (
                  <button
                    key={labelKey}
                    type="button"
                    className="bbf-garage-quick-capsule"
                    aria-label={label}
                    tabIndex={-1}
                  >
                    <span dir={direction}>{label}</span>
                  </button>
                )
              })}
            </div>
          )}
        </section>

        <section className="bbf-garage-actions">
          <div className="bbf-garage-actions-full">
            {GARAGE_ACTIONS.slice(0, 5).map(renderAction)}
          </div>
          <div className="bbf-garage-actions-grid">
            {GARAGE_ACTIONS.slice(5).map(renderAction)}
          </div>
          <div className="bbf-garage-terminal-row">
            <button
              className={`bbf-garage-terminal ${terminalMode}`}
              disabled={terminalDisabled}
              onClick={terminalAction}
              tabIndex={terminalAction ? 0 : -1}
              dir={direction}
            >{terminalLabel}</button>
          </div>
        </section>
      </main>

      {children}
          </div>
        </div>
      </div>
    </div>
  )
}
